import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { API } from "@/context/AuthContext";
import { BIZ } from "@/data/site";
import { logger } from "@/lib/logger";

/**
 * Google "G" mark — official-color SVG (no external asset needed).
 */
function GoogleG({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.44c-.28 1.4-1.09 2.59-2.32 3.39v2.82h3.75c2.19-2.02 3.62-5.02 3.62-8.45z" fill="#4285F4"/>
      <path d="M12 24c3.24 0 5.95-1.08 7.94-2.91l-3.75-2.82c-1.04.69-2.36 1.11-4.19 1.11-3.22 0-5.95-2.18-6.93-5.11H1.19v3.22C3.17 21.31 7.28 24 12 24z" fill="#34A853"/>
      <path d="M5.07 14.27c-.25-.72-.39-1.49-.39-2.27s.14-1.55.39-2.27V6.51H1.19C.43 8.02 0 9.96 0 12s.43 3.98 1.19 5.49l3.88-3.22z" fill="#FBBC05"/>
      <path d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.32-3.32C17.94 1.29 15.24 0 12 0 7.28 0 3.17 2.69 1.19 6.51l3.88 3.22C6.05 6.93 8.78 4.75 12 4.75z" fill="#EA4335"/>
    </svg>
  );
}

function StarRow({ value = 0, size = "w-4 h-4", filledClass = "text-yellow-400 fill-yellow-400", emptyClass = "text-stone-300" }) {
  return (
    <div className="flex gap-0.5" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${size} ${n <= Math.round(value) ? filledClass : emptyClass}`} />
      ))}
    </div>
  );
}

/**
 * Reusable inline Google-rating badge (avg + count + Google G).
 * Renders nothing until data has loaded.
 */
export function GoogleRatingBadge({ dark = false, className = "" }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/google-reviews`);
        if (alive && res.data?.connected) setData(res.data);
      } catch (e) {
        logger.warn("google-reviews badge fetch failed", e);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!data) return null;
  const base = dark
    ? "bg-white/5 border-white/10 text-white"
    : "bg-white border-stone-200 text-stone-900";

  return (
    <a
      href={data.google_url || BIZ.gbpUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="google-rating-badge"
      className={`inline-flex items-center gap-3 rounded-full border px-4 py-2 shadow-sm hover:shadow-md transition-all ${base} ${className}`}
    >
      <GoogleG className="w-4 h-4 shrink-0" />
      <span className="text-sm font-semibold">Google</span>
      <span className="h-4 w-px bg-current opacity-20" />
      <span className="font-bold" data-testid="google-rating-value">{Number(data.rating).toFixed(1)}</span>
      <StarRow value={data.rating} size="w-3.5 h-3.5" />
      <span className={`text-xs ${dark ? "text-stone-300" : "text-stone-500"}`}>
        (<span data-testid="google-rating-total">{data.total}</span>)
      </span>
    </a>
  );
}

/**
 * Full section: hero header + live Google review carousel.
 * `variant="dark"` uses the dark hero styling (LandingPage). `variant="light"` for ReviewsPage.
 */
export default function GoogleReviews({ variant = "dark" }) {
  const dark = variant === "dark";
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/google-reviews`);
        if (alive) setData(res.data || null);
      } catch (e) {
        logger.warn("google-reviews section fetch failed", e);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const reviews = data?.reviews || [];

  useEffect(() => {
    if (reviews.length < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % reviews.length), 7000);
    return () => clearInterval(t);
  }, [reviews.length]);

  if (!loaded) return null;
  if (!data?.connected || reviews.length === 0) {
    // Graceful fallback: keep the #reviews anchor and CTA to Google Business
    // Profile alive even when the Places API returns nothing (unreachable,
    // key restricted, or no reviews yet). The visitor is never sent to a
    // blank spot when they click the Reviews nav link.
    return (
      <section
        id="reviews"
        data-testid="google-reviews-section"
        className={`${dark ? "py-24 md:py-32 bg-stone-900 text-white" : "py-16 md:py-24 bg-white"} scroll-mt-24`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <div className={`text-xs font-bold uppercase tracking-[0.25em] mb-4 ${dark ? "text-brand-300" : "text-brand-700"}`}>
            Verified Google Reviews
          </div>
          <h2
            className={`text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight ${dark ? "text-white" : "text-stone-900"}`}
            style={{ fontFamily: "Outfit" }}
          >
            Loved by our neighbors.
          </h2>
          <p className={`mt-4 max-w-xl mx-auto ${dark ? "text-stone-300" : "text-stone-600"}`}>
            Read our full set of verified reviews directly on Google — and if we&apos;ve washed something for you,
            we&apos;d love a quick review too.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href={BIZ.gbpUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-see-all"
              className={`inline-flex items-center gap-2 rounded-full font-bold px-6 py-3 shadow-lg transition-all hover:-translate-y-0.5 ${
                dark ? "bg-white text-stone-900 hover:bg-brand-100" : "bg-brand-600 hover:bg-electric-500 text-white"
              }`}
            >
              <GoogleG className="w-4 h-4" />
              See our reviews on Google
              <ExternalLink className="w-4 h-4" />
            </a>
            <a
              href={BIZ.gbpUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-leave-review"
              className={`inline-flex items-center gap-2 rounded-full font-bold px-6 py-3 border transition-all hover:-translate-y-0.5 ${
                dark ? "border-white/20 text-white hover:bg-white/10" : "border-stone-300 text-stone-800 hover:bg-stone-100"
              }`}
            >
              Leave us a review
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    );
  }

  const r = reviews[index];
  const prev = () => setIndex((i) => (i - 1 + reviews.length) % reviews.length);
  const next = () => setIndex((i) => (i + 1) % reviews.length);

  const sectionCls = dark
    ? "py-24 md:py-32 bg-stone-900 text-white relative overflow-hidden"
    : "py-16 md:py-24 bg-white relative overflow-hidden";

  const cardCls = dark
    ? "bg-gradient-to-br from-white/8 to-white/3 border-white/10 backdrop-blur-sm"
    : "bg-stone-50 border-stone-200";

  const textCls = dark ? "text-stone-100" : "text-stone-800";
  const mutedCls = dark ? "text-stone-400" : "text-stone-500";
  const eyebrow = dark ? "text-brand-300" : "text-brand-700";
  const btnCls = dark
    ? "bg-white/5 hover:bg-white/10 border-white/10 text-white"
    : "bg-white hover:bg-stone-50 border-stone-200 text-stone-700";
  const dotIdle = dark ? "bg-white/5 border-white/15 hover:bg-white/10" : "bg-white border-stone-200 hover:bg-stone-50";
  const dotActive = dark ? "bg-white/10 border-brand-500/40" : "bg-brand-50 border-brand-300";

  return (
    <section
      id="reviews"
      data-testid="google-reviews-section"
      className={`${sectionCls} scroll-mt-24`}
    >
      {dark && (
        <>
          <div className="absolute inset-0 bg-grain opacity-15 pointer-events-none" />
          <div className="absolute top-12 right-12 w-72 h-72 bg-brand-500/20 rounded-full blur-3xl pointer-events-none" />
        </>
      )}

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-12">
          <div className="max-w-2xl">
            <div className={`text-xs font-bold uppercase tracking-[0.25em] mb-4 ${eyebrow}`}>
              Verified Google Reviews
            </div>
            <h2
              className={`text-4xl md:text-5xl font-extrabold tracking-tight leading-tight ${dark ? "text-white" : "text-stone-900"}`}
              style={{ fontFamily: "Outfit" }}
            >
              {reviews.length === 1 ? "What a neighbor said." : "What the neighborhood is saying."}
            </h2>
          </div>

          {/* Powered-by-Google summary badge */}
          <a
            href={data.google_url || BIZ.gbpUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-summary"
            className={`inline-flex items-center gap-4 px-5 py-3 rounded-full border shadow-sm hover:shadow-md transition-all self-start ${
              dark ? "bg-white/5 border-white/10 backdrop-blur-sm" : "bg-white border-stone-200"
            }`}
          >
            <GoogleG className="w-5 h-5 shrink-0" />
            <div className="flex items-center gap-2">
              <span className={`font-bold ${dark ? "text-white" : "text-stone-900"}`} data-testid="google-reviews-avg">
                {Number(data.rating).toFixed(1)}
              </span>
              <StarRow value={data.rating} size="w-4 h-4" />
            </div>
            <span className={`text-sm ${mutedCls}`}>
              <span className="font-semibold" data-testid="google-reviews-total">{data.total}</span>{" "}
              review{data.total === 1 ? "" : "s"}
            </span>
            <ExternalLink className={`w-3.5 h-3.5 ${mutedCls}`} />
          </a>
        </div>

        {/* Carousel */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8" data-testid="google-review-card">
            <div className={`relative p-8 md:p-12 rounded-3xl border min-h-[280px] flex flex-col ${cardCls}`}>
              <Quote className={`absolute top-6 right-6 w-16 h-16 ${dark ? "text-brand-500/20" : "text-brand-200"}`} />

              <div className="flex items-center gap-3 mb-6 pr-16 md:pr-20">
                {r.profile_photo ? (
                  <img
                    src={r.profile_photo}
                    alt={r.author}
                    referrerPolicy="no-referrer"
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-white/20"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-brand-500/20 flex items-center justify-center text-lg font-bold">
                    {(r.author || "?").charAt(0)}
                  </div>
                )}
                <div>
                  <div className={`font-bold ${dark ? "text-white" : "text-stone-900"}`} data-testid="google-review-author">
                    {r.author}
                  </div>
                  <div className={`text-xs ${mutedCls}`}>{r.relative_time}</div>
                </div>
                <div className="ml-auto">
                  <StarRow value={r.rating} />
                </div>
              </div>

              <p
                data-testid="google-review-text"
                className={`text-xl md:text-2xl leading-relaxed flex-1 font-medium ${textCls}`}
                style={{ fontFamily: "Outfit" }}
              >
                &ldquo;{r.text}&rdquo;
              </p>

              <div className={`mt-8 pt-6 border-t flex items-center justify-between gap-3 ${dark ? "border-white/10" : "border-stone-200"}`}>
                <div className="flex items-center gap-2">
                  <GoogleG className="w-4 h-4" />
                  <span className={`text-xs font-semibold uppercase tracking-wider ${mutedCls}`}>
                    Posted on Google
                  </span>
                </div>
                <a
                  href={data.google_url || BIZ.gbpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="google-review-view-link"
                  className={`text-xs font-semibold inline-flex items-center gap-1 hover:underline ${dark ? "text-brand-300" : "text-brand-700"}`}
                >
                  View on Google <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 flex lg:flex-col gap-3 items-stretch">
            {reviews.length > 1 && (
              <div className="flex lg:flex-col gap-3">
                <button
                  onClick={prev}
                  data-testid="google-reviews-prev-btn"
                  className={`w-12 h-12 rounded-full border flex items-center justify-center transition-colors ${btnCls}`}
                  aria-label="Previous review"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={next}
                  data-testid="google-reviews-next-btn"
                  className={`w-12 h-12 rounded-full border flex items-center justify-center transition-colors ${btnCls}`}
                  aria-label="Next review"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}

            <div className="flex-1 grid grid-cols-1 gap-2 self-stretch">
              {reviews.slice(0, 4).map((rv, i) => (
                <button
                  key={`${rv.author}-${i}`}
                  onClick={() => setIndex(i)}
                  data-testid={`google-review-dot-${i}`}
                  className={`text-left p-3 rounded-2xl border transition-all ${
                    i === index ? dotActive : dotIdle
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <StarRow value={rv.rating} size="w-2.5 h-2.5" />
                    <span className={`text-xs font-semibold truncate ${dark ? "text-stone-200" : "text-stone-700"}`}>
                      {rv.author}
                    </span>
                  </div>
                  <p className={`mt-1.5 text-xs line-clamp-2 ${mutedCls}`}>
                    {rv.text}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* CTA row */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <a
            href={data.google_url || BIZ.gbpUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-see-all"
            className={`inline-flex items-center gap-2 rounded-full font-bold px-6 py-3 shadow-lg transition-all hover:-translate-y-0.5 ${
              dark ? "bg-white text-stone-900 hover:bg-brand-100" : "bg-brand-600 hover:bg-electric-500 text-white"
            }`}
          >
            <GoogleG className="w-4 h-4" />
            See all reviews on Google
            <ExternalLink className="w-4 h-4" />
          </a>
          <a
            href={data.google_url || BIZ.gbpUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-leave-review"
            className={`inline-flex items-center gap-2 rounded-full font-bold px-6 py-3 border transition-all hover:-translate-y-0.5 ${
              dark ? "border-white/20 text-white hover:bg-white/10" : "border-stone-300 text-stone-800 hover:bg-stone-100"
            }`}
          >
            Leave us a review
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
